import { useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import {
	deleteRelatedPerson,
	getPersonRelatedPeople,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useRelatedPeople.ts';
import { mapResponseToRelatedPersonRows } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/relatedPersonsSection.utils.ts';
import { RelatedPersonRow } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/RelatedPersonsSection.types.ts';

export const useRelatedPersons = () => {
	const { selectedEntityId, selectedEntityData } = useContext(CapManageControllerContext);
	const { dealId } = useContext(DealContext);
	const queryClient = useQueryClient();

	const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
	const [rowToDelete, setRowToDelete] = useState<RelatedPersonRow | null>(null);

	const {
		data: relatedPersons = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ['relatedPeople', selectedEntityId],
		queryFn: () => getPersonRelatedPeople(selectedEntityId),
		enabled: !!selectedEntityId,
		select: mapResponseToRelatedPersonRows,
	});

	const invalidateRelatedPeople = () =>
		queryClient.invalidateQueries({ queryKey: ['relatedPeople', selectedEntityId] });

	const removeMutation = useMutation({ mutationFn: deleteRelatedPerson });

	const confirmRemoveRelatedPerson = () => {
		if (!rowToDelete) {
			return;
		}

		removeMutation.mutate(
			{
				personId: selectedEntityId,
				body: {
					relatedPersonId: rowToDelete.personId,
					relationType: rowToDelete.relationType,
				},
			},
			{
				onSuccess: () => {
					void invalidateRelatedPeople();
					setRowToDelete(null);
				},
			}
		);
	};

	return {
		relatedPersons,
		isLoading: isLoading || isFetching,
		selectedEntityId,
		selectedEntityType: selectedEntityData?.type,
		dealId,
		isAddDrawerOpen,
		openAddDrawer: () => setIsAddDrawerOpen(true),
		closeAddDrawer: () => setIsAddDrawerOpen(false),
		rowToDelete,
		setRowToDelete,
		confirmRemoveRelatedPerson,
		removeLoading: removeMutation.isPending,
		removeSuccess: removeMutation.isSuccess,
		invalidateRelatedPeople,
	};
};
